import React from 'react';
import { usePopupClose } from '../Utils/usePopupClose';
import { 
  getCheatingProbabilityTextColorClass, 
  getCheatingProbabilityLabel,
  getCheatingProbabilityBgColorClass
} from '../Utils/ColorUtils';

/**
 * CheatingDetailsModal Component
 * Displays a detailed modal with cheating assessment data and events
 * 
 * @param {Object} cheatingData - The data object containing all cheating-related information
 * @param {number} completionTime - Time taken to complete in minutes
 * @param {boolean} show - Whether to show the modal
 * @param {Function} onClose - Function to call when closing the modal
 */
const CheatingDetailsModal = ({ 
  cheatingData,
  completionTime = null,
  show = false,
  onClose = () => {}
}) => {
  // Use the popup close hook to handle clicking outside and ESC key
  const popupRef = usePopupClose(show, onClose);
  
  // Extract data from cheatingData with generic naming
  const finalCheatingAnalysis = cheatingData?.final_cheating_analysis;
  const cheatingAssessment = cheatingData?.cheating_assessment_json;
  const cheatingEvents = cheatingData?.cheating_events_json || [];
  const voiceAnalysisAssessment = cheatingData?.voice_analysis_assessment_json;
  
  // Get probability and rationale from final_cheating_analysis
  const probabilityValue = finalCheatingAnalysis?.final_cheating_probability 
    ? Math.round(finalCheatingAnalysis.final_cheating_probability * 100)
    : 0;
  const rationale = finalCheatingAnalysis?.rationale || [];

  if (!show) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 overflow-visible">
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/30 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div ref={popupRef} className="relative z-10 bg-white rounded-lg shadow-xl w-[95%] h-[95%] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="absolute top-0 right-0 left-0 z-20 p-4 flex justify-end items-center bg-white/70 backdrop-blur-md">
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 focus:outline-none"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        
        {/* Content */}
        <div className="overflow-auto flex-grow p-6 pt-14">
          {!finalCheatingAnalysis && !cheatingAssessment ? (
            <div className="text-center py-8">
              <p className="text-gray-500">No cheating assessment data available.</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Title Section */}
              <div className="text-center py-12 mb-8">
                <h1 className="text-4xl font-bold text-gray-900 mb-6">Integrity Report</h1>
                <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                  Comprehensive analysis of candidate behavior during evaluation, including probability assessment, event tracking, and voice analysis metrics.
                </p>
              </div>

              {/* Top Section: Probability (Left) + Conclusion/Rationale (Right) */}
              <div className="bg-white border border-gray-300 rounded-2xl p-6">
                <div className="grid grid-cols-1 md:grid-cols-[auto,1fr] gap-12">
                  {/* Left: Circular Probability Indicator */}
                  <div className="flex flex-col items-center justify-center space-y-4 p-6">
                    {/* Circular indicator */}
                    <div className="relative w-40 h-40">
                      <svg className="w-full h-full transform -rotate-90">
                        <circle
                          cx="80"
                          cy="80"
                          r="70"
                          stroke="#e5e7eb"
                          strokeWidth="12"
                          fill="none"
                        />
                        <circle
                          cx="80"
                          cy="80"
                          r="70"
                          stroke={probabilityValue > 70 ? 'var(--color-brand-red)' : probabilityValue > 40 ? 'var(--color-brand-yellow)' : 'var(--color-brand-green)'}
                          strokeWidth="12"
                          fill="none"
                          strokeDasharray={`${(probabilityValue / 100) * 439.6} 439.6`}
                          strokeLinecap="round"
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className={`text-4xl font-bold ${getCheatingProbabilityTextColorClass(probabilityValue)}`}>
                          {probabilityValue}%
                        </span>
                      </div>
                    </div>
                    
                    {/* Labels */}
                    <div className="text-center space-y-2">
                      <h3 className="text-2xl font-bold text-gray-900">Integrity Risk Score</h3>
                      {completionTime !== null && (
                        <div className="flex items-center justify-center text-gray-600">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>Completed in: {completionTime} minutes</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Right: Conclusion & Rationale */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-4">Conclusion & Rationale</h3>
                    {rationale && rationale.length > 0 ? (
                      <div className="space-y-4">
                        {rationale.map((reason, index) => (
                          <p key={index} className="text-gray-700 text-lg leading-relaxed">
                            {reason}
                          </p>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 italic">No rationale provided.</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Event Pattern Recognition Section */}
              {cheatingEvents && cheatingEvents.length > 0 && (() => {
                // Count occurrences of each event type
                const eventTypeCounts = cheatingEvents.reduce((acc, event) => {
                  const formattedType = event.type
                    .split('-')
                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ');
                  acc[formattedType] = (acc[formattedType] || 0) + 1;
                  return acc;
                }, {});

                return (
                  <div className="bg-white border border-gray-300 rounded-2xl p-6 space-y-6">
                    <h2 className="text-2xl font-bold text-gray-900">Event pattern recognition</h2>
                    
                    {/* Total Events and Event Type Breakdown */}
                    <div className="flex gap-6">
                      {/* Left: Total Events */}
                      <div className="bg-blue-100 rounded-2xl p-8 flex flex-col items-center justify-center min-w-[200px]">
                        <div className="text-6xl font-bold text-blue-700 mb-3">{cheatingEvents.length}</div>
                        <div className="text-lg font-semibold text-blue-900">Total Events</div>
                      </div>
                      
                      {/* Right: Event Type Breakdown */}
                      <div className="rounded-2xl p-6 flex-1 flex items-center" style={{ backgroundColor: 'var(--color-brand-blue-light)' }}>
                        <div className="flex gap-16 flex-wrap justify-between items-center w-full">
                          {Object.entries(eventTypeCounts).map(([eventType, count]) => {
                            // Determine icon based on event type
                            let icon;
                            const lowerType = eventType.toLowerCase();
                            
                            if (lowerType.includes('tab') || lowerType.includes('switch')) {
                              // Inline SVG for tab/window switching (overlapping windows)
                              icon = (
                                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="3" y="3" width="18" height="10" rx="2" ry="2" opacity="0.3"/>
                                  <rect x="5" y="8" width="18" height="10" rx="2" ry="2"/>
                                </svg>
                              );
                            } else if (lowerType.includes('window') || lowerType.includes('blur')) {
                              icon = <img src="/img/icons/blur_on.svg" alt={eventType} className="w-8 h-8" />;
                            } else if (lowerType.includes('copy')) {
                              icon = <img src="/img/icons/file_copy.svg" alt={eventType} className="w-8 h-8" />;
                            } else if (lowerType.includes('paste')) {
                              icon = <img src="/img/icons/document_scanner.svg" alt={eventType} className="w-8 h-8" />;
                            } else if (lowerType.includes('devtool') || lowerType.includes('developer')) {
                              icon = <img src="/img/icons/construction.svg" alt={eventType} className="w-8 h-8" />;
                            } else {
                              // Default fallback icon
                              icon = <img src="/img/icons/blur_on.svg" alt={eventType} className="w-8 h-8" />;
                            }
                            
                            return (
                              <div key={eventType} className="flex flex-col items-center gap-3 min-w-[160px]">
                                <div className="flex items-baseline justify-center gap-4">
                                  <div className="text-gray-700">
                                    {icon}
                                  </div>
                                  <div className="text-5xl font-normal text-gray-900 leading-[1]">{count}</div>
                                </div>
                                <div className="text-lg font-regular text-gray-700 text-center">{eventType}</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                    
                    {/* Heuristics Section */}
                    <div>
                      <h3 className="text-lg font-semibold mb-3 text-gray-900 uppercase tracking-wide">Insights</h3>
                      <div className="grid grid-cols-3 gap-4 mb-6">
                        {/* Answer is Pasted */}
                        {cheatingAssessment?.heuristic_answerIsPasted !== undefined && (
                          <div 
                            className="border rounded-lg p-6 relative"
                            style={{
                              borderColor: cheatingAssessment.heuristic_answerIsPasted 
                                ? 'var(--color-brand-red)' 
                                : 'var(--color-brand-green)',
                              backgroundColor: cheatingAssessment.heuristic_answerIsPasted 
                                ? 'var(--color-brand-red-light)' 
                                : 'var(--color-brand-green-light)'
                            }}
                          >
                            <div 
                              className="text-6xl font-regular text-center mb-2"
                              style={{
                                color: cheatingAssessment.heuristic_answerIsPasted 
                                  ? 'var(--color-brand-red)' 
                                  : 'var(--color-brand-green)'
                              }}
                            >
                              {cheatingAssessment.heuristic_answerIsPasted ? 'True' : 'False'}
                            </div>
                            <div className="text-center text-gray-900 font-semibold">Answers were Pasted</div>
                          </div>
                        )}
                        
                        {/* Placeholder 2 */}
                        <div className="border border-gray-300 rounded-lg p-6 bg-gray-50 relative">
                          <div className="absolute top-2 right-2 bg-yellow-200 text-yellow-800 text-xs px-2 py-1 rounded font-semibold">
                            Comin Soon
                          </div>
                          <div className="text-4xl font-bold text-gray-400 text-center mb-2">
                            —
                          </div>
                          <div className="text-center text-gray-500 font-semibold">Insight 2</div>
                        </div>
                        
                        {/* Placeholder 3 */}
                        <div className="border border-gray-300 rounded-lg p-6 bg-gray-50 relative">
                          <div className="absolute top-2 right-2 bg-yellow-200 text-yellow-800 text-xs px-2 py-1 rounded font-semibold">
                            Comin Soon
                          </div>
                          <div className="text-4xl font-bold text-gray-400 text-center mb-2">
                            —
                          </div>
                          <div className="text-center text-gray-500 font-semibold">Insight 3</div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Events Table */}
                    <div>
                      <div className="overflow-x-auto max-h-[400px] overflow-y-auto relative">
                        <table className="min-w-full">
                          <thead className="sticky top-0 z-10" style={{ backgroundColor: '#E8EEF5' }}>
                            <tr>
                              <th scope="col" className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wide w-[180px]">
                                Event Type
                              </th>
                              <th scope="col" className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wide w-[220px]">
                                Timestamp
                              </th>
                              <th scope="col" className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wide">
                                Content
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white">
                            {cheatingEvents.map((event, index) => {
                              // Format the timestamp to be more readable
                              const date = new Date(event.timestamp);
                              const formattedDate = date.toLocaleDateString();
                              const formattedTime = date.toLocaleTimeString([], { 
                                hour: '2-digit', 
                                minute: '2-digit', 
                                second: '2-digit' 
                              });
                              
                              // Format the event type to be more readable
                              const formattedType = event.type
                                .split('-')
                                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                .join(' ');
                              
                              return (
                                <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                  <td className="px-6 py-4 whitespace-nowrap text-lg font-medium text-gray-900 w-[180px]">
                                    {formattedType}
                                  </td>
                                  <td className="px-6 py-4 whitespace-nowrap text-lg text-gray-500 w-[220px]">
                                    {formattedDate}, {formattedTime}
                                  </td>
                                  <td className="px-6 py-4 text-lg text-gray-500">
                                    {event.content ? (
                                      <div className="bg-gray-50 p-3 rounded text-sm max-h-24 overflow-y-auto">
                                        <pre className="whitespace-pre-wrap font-mono text-sm">
                                          {event.content}
                                        </pre>
                                      </div>
                                    ) : (
                                      <span className="text-gray-400 italic">No content</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Voice Analysis Details Section */}
              {voiceAnalysisAssessment && (
                <div className="bg-white border border-gray-300 rounded-2xl p-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Voice analysis details</h2>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-[280px,1fr] gap-8">
                    {/* Left Column: Key Metrics */}
                    <div className="space-y-6">
                      {/* Identified Speakers */}
                      <div 
                        className="border rounded-xl py-8 px-6"
                        style={{
                          borderColor: (voiceAnalysisAssessment.speakers?.length || 0) > 1 
                            ? 'var(--color-brand-red)' 
                            : '#22D3EE',
                          backgroundColor: (voiceAnalysisAssessment.speakers?.length || 0) > 1 
                            ? '#FEF6F6' 
                            : '#ECFEFF'
                        }}
                      >
                        <div 
                          className="text-6xl text-center mb-3"
                          style={{
                            color: (voiceAnalysisAssessment.speakers?.length || 0) > 1 
                              ? 'var(--color-brand-red)' 
                              : '#0891B2'
                          }}
                        >
                          {voiceAnalysisAssessment.speakers?.length || 0}
                        </div>
                        <div className="text-center text-gray-900 text-xl">Identified Speakers</div>
                      </div>
                      
                      {/* Reading off screen */}
                      {voiceAnalysisAssessment.reading_vs_thinking_hint && (
                        <div 
                          className="border rounded-xl py-8 px-6"
                          style={{
                            borderColor: voiceAnalysisAssessment.reading_vs_thinking_hint.likely_reading 
                              ? 'var(--color-brand-red)' 
                              : 'var(--color-brand-green)',
                            backgroundColor: voiceAnalysisAssessment.reading_vs_thinking_hint.likely_reading 
                              ? '#FEF6F6' 
                              : 'var(--color-brand-green-light)'
                          }}
                        >
                          <div 
                            className="text-6xl text-center mb-3"
                            style={{
                              color: voiceAnalysisAssessment.reading_vs_thinking_hint.likely_reading 
                                ? 'var(--color-brand-red)' 
                                : 'var(--color-brand-green)'
                            }}
                          >
                            {voiceAnalysisAssessment.reading_vs_thinking_hint.likely_reading ? 'True' : 'False'}
                          </div>
                          <div className="text-center text-gray-900 text-xl">Reading From Screen</div>
                          {/* {voiceAnalysisAssessment.reading_vs_thinking_hint.reason && (
                            <div className="text-sm text-gray-600 text-center mt-2">
                              {voiceAnalysisAssessment.reading_vs_thinking_hint.reason}
                            </div>
                          )} */}
                        </div>
                      )}
                      
                      {/* Total Speech Duration */}
                      {voiceAnalysisAssessment.total_speech_sec && (
                        <div className="border rounded-xl py-8 px-6" style={{ borderColor: '#60A5FA', backgroundColor: 'var(--color-brand-blue-light)' }}>
                          <div className="text-6xl text-center mb-3" style={{ color: '#2563EB' }}>
                            {Math.round(voiceAnalysisAssessment.total_speech_sec)}s
                          </div>
                          <div className="text-center text-gray-900 text-xl">Total Speech</div>
                        </div>
                      )}
                    </div>
                    
                    {/* Right Column: Voice Behaviour Box (contains grid + demographics) */}
                    <div className="border rounded-xl p-6" style={{ borderColor: '#B8D4E8' }}>
                      {/* Title */}
                      <div className="text-center mb-6">
                        <h3 className="text-2xl text-gray-900">Vocal analytics metrics</h3>
                      </div>
                      
                      {/* Voice behaviour 2x3 grid */}
                      <div className="mb-8">
                        <div className="grid grid-cols-3">
                          {/* Row 1: Engagement, Hesitation, Strength */}
                          {voiceAnalysisAssessment.dominant_engagement && (
                            <div className="p-8 text-center" style={{ borderRight: '1px solid #B8D4E8' }}>
                              <div className="text-3xl font-semibold text-gray-900 capitalize mb-2">
                                {voiceAnalysisAssessment.dominant_engagement.label}
                              </div>
                              <div className="text-base text-gray-600">Engagement</div>
                            </div>
                          )}
                          
                          {voiceAnalysisAssessment.hesitation_rate && (
                            <div className="p-8 text-center" style={{ borderRight: '1px solid #B8D4E8' }}>
                              <div className="text-3xl font-semibold text-gray-900 mb-2">
                                {voiceAnalysisAssessment.hesitation_rate.yes_share > voiceAnalysisAssessment.hesitation_rate.no_share ? 'Yes' : 'No'}
                              </div>
                              <div className="text-base text-gray-600">Hesitation</div>
                            </div>
                          )}
                          
                          {voiceAnalysisAssessment.dominant_strength && (
                            <div className="p-8 text-center">
                              <div className="text-3xl font-semibold text-gray-900 capitalize mb-2">
                                {voiceAnalysisAssessment.dominant_strength.label}
                              </div>
                              <div className="text-base text-gray-600">Strength</div>
                            </div>
                          )}
                        </div>
                        
                        {/* Horizontal Divider */}
                        <div style={{ borderTop: '1px solid #B8D4E8' }}></div>
                        
                        <div className="grid grid-cols-3">
                          {/* Row 2: Speaking rate, Emotion, Positivity */}
                          {voiceAnalysisAssessment.dominant_speaking_rate && (
                            <div className="p-8 text-center" style={{ borderRight: '1px solid #B8D4E8' }}>
                              <div className="text-3xl font-semibold text-gray-900 capitalize mb-2">
                                {voiceAnalysisAssessment.dominant_speaking_rate.label}
                              </div>
                              <div className="text-base text-gray-600">Speaking rate</div>
                            </div>
                          )}
                          
                          {voiceAnalysisAssessment.dominant_emotion && (
                            <div className="p-8 text-center" style={{ borderRight: '1px solid #B8D4E8' }}>
                              <div className="text-3xl font-semibold text-gray-900 capitalize mb-2">
                                {voiceAnalysisAssessment.dominant_emotion.label}
                              </div>
                              <div className="text-base text-gray-600">Emotion</div>
                            </div>
                          )}
                          
                          {voiceAnalysisAssessment.dominant_positivity && (
                            <div className="p-8 text-center">
                              <div className="text-3xl font-semibold text-gray-900 capitalize mb-2">
                                {voiceAnalysisAssessment.dominant_positivity.label}
                              </div>
                              <div className="text-base text-gray-600">Positivity</div>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Demographics Row (inside voice behaviour box) */}
                      <div className="grid grid-cols-3 gap-4">
                        {/* Gender */}
                        {voiceAnalysisAssessment.dominant_gender && (
                          <div className="bg-gray-100 rounded-lg p-6 text-center">
                            <div className="text-2xl font-bold text-gray-900 capitalize mb-1">
                              {voiceAnalysisAssessment.dominant_gender.label}
                            </div>
                            <div className="text-sm text-gray-600">Gender</div>
                          </div>
                        )}
                        
                        {/* Age */}
                        {voiceAnalysisAssessment.dominant_age && (
                          <div className="bg-gray-100 rounded-lg p-6 text-center">
                            <div className="text-2xl font-bold text-gray-900 mb-1">
                              {voiceAnalysisAssessment.dominant_age.label}
                            </div>
                            <div className="text-sm text-gray-600">Age</div>
                          </div>
                        )}
                        
                        {/* Language */}
                        {voiceAnalysisAssessment.dominant_language && (
                          <div className="bg-gray-100 rounded-lg p-6 text-center">
                            <div className="text-2xl font-bold text-gray-900 uppercase mb-1">
                              {voiceAnalysisAssessment.dominant_language.label}
                            </div>
                            <div className="text-sm text-gray-600">Language</div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CheatingDetailsModal;
